import React, { useEffect, useRef, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, Route, Redirect, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { initializeAnalytics } from '@offerzen/pinata-shame'
import { notify } from '@offerzen/react-utils'

import Body from './components/Body'
import ScrollTop from './components/ScrollTop'
import Navbar, { navbarHeight } from './components/Navbar'
import PopulateInitialState from './components/PopulateInitialState'
import SystemErrorRedux from './components/SystemErrorRedux'
import LoadingScene from './scenes/LoadingScene'
import ErrorScene from './scenes/ErrorScene'
import AdminPanel from './components/AdminPanel'
import CurrentUser from './containers/CurrentUser'

import { initialiseCurrentUser } from './reducks/initialState'

import { getCurrentUser } from './apiModules/currentUser'
import { errorUser as errorUserSelector } from './selectors/currentUser'

import { isOnboardingOnlyRoute, isProtectedRoute } from './helpers/redirectHelpers'
import { identifyCandidate } from './helpers/analyticsHelpers'
import { notFoundReactQuery } from './helpers/errorHelpers'
import { lazyWithRetry } from './helpers/lazyHelpers'
import { useFetchCandidateProfile } from './hooks'
import RedirectToNewProfilePage from './components/RedirectToNewProfilePage'

import './styles'

const HomeScene = lazyWithRetry(() => import('./scenes/HomeScene'))
const OnboardingMain = lazyWithRetry(() => import('../Onboarding/Main'))
const SocialSignupComplete = lazyWithRetry(() => import('../Onboarding/scenes/SocialSignupComplete'))
const InterviewRequestsMain = lazyWithRetry(() => import('../InterviewRequests/Main'))
const DashboardMain = lazyWithRetry(() => import('../Dashboard/Main'))
const ProfileMain = lazyWithRetry(() => import('../Profile/Main'))

const CandidateExperienceMain = () => {
  const dispatch = useDispatch()
  const errorUser = useSelector(state => errorUserSelector(state))

  const setCurrentUser = currentUser => {
    dispatch(initialiseCurrentUser(currentUser))
  }
  const {
    data: currentUser,
    status: userFetchStatus,
    refetch: refetchCurrentUser,
  } = useQuery(['currentUser'], () => getCurrentUser(), {
    refetchOnWindowFocus: false,
    onSuccess: data => {
      setCurrentUser(data)
    },
  })

  const isLoggedIn = Boolean(currentUser)
  const candidateProfileId = isLoggedIn ? currentUser.profile_id : undefined

  const {
    data,
    status: candidateProfileFetchStatus,
    error: candidateProfileFetchError,
  } = useFetchCandidateProfile({ candidateProfileId, refetchOnWindowFocus: false, enabled: isLoggedIn })

  const isOnboarding = isLoggedIn && data && data.candidateProfile.currentState === 'ONBOARDING'

  const analyticsInitialised = useRef(false)
  useEffect(() => {
    if (!analyticsInitialised.current) {
      initializeAnalytics(process.env.REACT_APP_SEGMENT_WRITE_KEY)
      analyticsInitialised.current = true
    }
  }, [analyticsInitialised])

  useEffect(() => {
    if (data && currentUser) {
      identifyCandidate(currentUser, data.candidateProfile)
    }
  }, [data, currentUser])

  const location = useLocation()

  if (userFetchStatus === 'loading' || candidateProfileFetchStatus === 'loading') return <LoadingScene />

  if (userFetchStatus === 'error' || candidateProfileFetchStatus === 'error') {
    // For unknown reasons, sometimes the `GraphQLController` unexpectedly returns a 401
    // before the `Candidate::BaseController` is able to respond with a 404.
    // Unfortunately, not all GraphQL response errors share the same structure, e.g.
    // the 401 returned from `GraphQLController` does not match the structure returned
    // for the 404 from the `Candidate::BaseController`. This causes the `notFoundReactQuery`
    // helper to throw an error. In this case, we would rather display a generic error page
    // to the user and log the error in Sentry.
    let errorCode
    try {
      errorCode = notFoundReactQuery(candidateProfileFetchError) ? 404 : undefined
    } catch (err) {
      notify(err, errorUser)
    }

    return <ErrorScene errorCode={errorCode} />
  }

  // If the user is not logged in and they're trying to access a protected route, redirect to sign in
  if (!isLoggedIn && isProtectedRoute(location.pathname)) {
    // Uses a path instead of the full URL because of a redirect after social auth log in explicitly expecting a path.
    // See https://github.com/offerzen/zadev/blob/8969c25fda4954c9da8f8236a8661f15b9689547/app/assets/javascripts/pages/devise/social-signup.js#L129.
    const fullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    window.location.replace(`${process.env.REACT_APP_API_URL}/users/js_redirect?url=${encodeURIComponent(fullPath)}`)
    return <LoadingScene />
  }

  const showAdminPanel = isLoggedIn && currentUser.category === 'admin'

  return (
    <Suspense fallback={<LoadingScene />}>
      <Switch>
        <Route exact path="/">
          <HomeScene candidateProfileId={candidateProfileId} />
        </Route>

        {/* Social auth for sign in AND sign up */}
        <Route exact path="/candidate/social-auth" component={SocialSignupComplete} />

        <Choose>
          <When condition={!isLoggedIn || isOnboarding}>
            <Route
              path="/candidate"
              render={() => (
                <PopulateInitialState candidateProfileId={candidateProfileId}>
                  <OnboardingMain refetchCurrentUser={refetchCurrentUser} />
                  <If condition={showAdminPanel}>
                    <AdminPanel candidateProfileId={candidateProfileId} />
                  </If>
                </PopulateInitialState>
              )}
            />
          </When>

          <Otherwise>
            <Route
              path="/candidate/:candidateSlug"
              render={routeProps => (
                <Choose>
                  {/* Redirect onboarded users away from onboarding routes */}
                  <When condition={isOnboardingOnlyRoute(routeProps.match.params.candidateSlug, routeProps.location.pathname)}>
                    <Redirect to={`/candidate/${candidateProfileId}/dashboard`} />
                  </When>
                  <When
                    condition={
                      routeProps.location.pathname === `/candidate/${routeProps.match.params.candidateSlug}` ||
                      routeProps.location.pathname === `/candidate/${routeProps.match.params.candidateSlug}/profile`
                    }>
                    <RedirectToNewProfilePage profileId={routeProps.match.params.candidateSlug} />
                  </When>
                  <Otherwise>
                    {/* We are passing in the slug instead of the ID for impersonation purposes */}
                    <PopulateInitialState candidateProfileId={routeProps.match.params.candidateSlug}>
                      <CurrentUser>
                        {userProps => (
                          <Navbar
                            metadata={userProps.metadata}
                            trackVisibilityStatus={userProps.trackVisibilityStatus}
                            signOut={userProps.signOut}
                          />
                        )}
                      </CurrentUser>

                      <Body>
                        <ScrollTop location={routeProps.location}>
                          <SystemErrorRedux offsetTop={navbarHeight} />
                          <Switch>
                            <Route path="/candidate/:candidateSlug/interview_requests" component={InterviewRequestsMain} />
                            <Route path="/candidate/:candidateSlug/dashboard" component={DashboardMain} />
                            {/* Apply is retired, but candidates still have these URLs bookmarked and in
                                old emails, so they land on the dashboard rather than a 404. Deliberately
                                not `exact`: the `/jobs/:jobId` deep link has to redirect too.
                                Spreading routeProps.location preserves the query parameters those old
                                email links carry, which the dashboard reads for UTM capture. */}
                            <Redirect
                              from="/candidate/:candidateSlug/jobs"
                              to={{ ...routeProps.location, pathname: '/candidate/:candidateSlug/dashboard' }}
                            />
                            <Redirect
                              from="/candidate/:candidateSlug/applications"
                              to={{ ...routeProps.location, pathname: '/candidate/:candidateSlug/dashboard' }}
                            />

                            <Route path="/candidate/:candidateSlug" component={ProfileMain} />
                          </Switch>
                        </ScrollTop>
                      </Body>
                      <If condition={showAdminPanel}>
                        <AdminPanel />
                      </If>
                    </PopulateInitialState>
                  </Otherwise>
                </Choose>
              )}
            />
          </Otherwise>
        </Choose>
      </Switch>
    </Suspense>
  )
}

export default CandidateExperienceMain
